import React, { useState, useCallback } from "react";
import InstructionInputCompact from "./instruction-input-compact";
import InstructionInputCE from "./instruction-input-ce";
import { useRecoilValue } from "recoil";
import { promptOverridesState, platformState } from "../global-state";
import { getEntityTypes, getEntities } from "../service/assistant-service";
import {
  listQuickWords,
  listSuggestedQuickWords,
} from "../service/quick-word-service";
import { useWebSocket } from "../ws/websocket";

const InstructionInputWrapper = ({
  placeholder = "Type a message...",
  onSendMessage,
  onStop,
  isGenerating = false,
}) => {
  const [entityTypesCache, setEntityTypesCache] = useState(null);
  const [entitiesCache, setEntitiesCache] = useState({});

  const promptOverrides = useRecoilValue(promptOverridesState);
  const platform = useRecoilValue(platformState);
  const { sendAndWaitWs } = useWebSocket();

  // Data fetchers for native bridge - connecting to your existing data sources
  const dataFetchers = {
    fetchEntityTypes: useCallback(async () => {
      try {
        if (entityTypesCache) return entityTypesCache;
        const types = await getEntityTypes(sendAndWaitWs);
        setEntityTypesCache(types);
        return types;
      } catch (error) {
        console.error("Error fetching entity types:", error);
        return [];
      }
    }, [entityTypesCache]),

    fetchEntities: useCallback(
      async (entityType) => {
        try {
          if (entitiesCache[entityType]) return entitiesCache[entityType];
          const entities = await getEntities(sendAndWaitWs, entityType);
          setEntitiesCache((prev) => ({ ...prev, [entityType]: entities }));
          return entities;
        } catch (error) {
          console.error("Error fetching entities:", error);
          return [];
        }
      },
      [entitiesCache]
    ),

    fetchQuickWords: useCallback(async () => {
      try {
        const [quickWordResp, suggestedQuickWordResp] = await Promise.all([
          listQuickWords(sendAndWaitWs),
          listSuggestedQuickWords(sendAndWaitWs),
        ]);

        // Process quick words with source information
        const regularQuickWords = Array.isArray(quickWordResp.quickWords)
          ? quickWordResp.quickWords.map((w) => ({
              text: w.quickWord,
              source: "quickword",
              type: "regular",
            }))
          : [];

        // Process suggested quick words with source information
        const suggestedQuickWords = Array.isArray(
          suggestedQuickWordResp.quickWords
        )
          ? suggestedQuickWordResp.quickWords.map((w) => ({
              text: w.quickWord || w,
              source: "quickword",
              type: "suggested",
            }))
          : [];

        return [...regularQuickWords, ...suggestedQuickWords];
      } catch (error) {
        console.error("Error fetching quick words:", error);
        return [];
      }
    }, []),
  };

  if (platform.source !== "app" || platform.os === "ios") {
    // Show full input directly if not in compact mode
    return (
      <div style={{ marginBottom: "8px", background: "transparent" }}>
        <InstructionInputCE
          placeholder={placeholder}
          onSendMessage={onSendMessage}
          onStop={onStop}
          isGenerating={isGenerating}
          dataFetchers={dataFetchers}
        />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "8px", background: "transparent" }}>
      {/* Compact WeChat-style input */}
      <InstructionInputCompact
        placeholder={placeholder}
        onSendMessage={onSendMessage}
        onStop={onStop}
        isGenerating={isGenerating}
        prompts={promptOverrides}
        dataFetchers={dataFetchers}
      />
    </div>
  );
};

export default InstructionInputWrapper;
